import { state, getCurrentServerPeople } from '../state';
import { openProfileCard } from '../components/profile-card';
import { showContextMenu } from '../components/context-menu';
import type { ContextMenuItem } from '../components/context-menu';
import { showToast } from '../components/toast';
import { iconX } from '../icons';
import type { Person, PresenceState } from '../types';
import { avatarBg } from '../util';

/** Resolve a person's 4-state presence with a fallback for backends that
 *  haven't shipped the dedicated field yet. Owners + admins get `dnd` mapped
 *  through unchanged when present; absence falls back to status. */
function presenceOf(p: Person): PresenceState {
  if (p.presence) return p.presence;
  return p.status === 'active' ? 'online' : 'offline';
}

const COLLAPSE_KEY_PREFIX = 'hub-people-ml-collapsed:';
function collapsedKey(serverId: string): string { return COLLAPSE_KEY_PREFIX + serverId; }
function loadCollapsed(serverId: string): Set<string> {
  try {
    const raw = localStorage.getItem(collapsedKey(serverId));
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch { return new Set(); }
}
function saveCollapsed(serverId: string, set: Set<string>): void {
  try { localStorage.setItem(collapsedKey(serverId), JSON.stringify([...set])); } catch { /* noop */ }
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

/** Render the Discord-style member list (right panel) */
export function renderMemberList(container: HTMLElement): void {
  const sid = state.currentServer;
  if (!sid) {
    // Belt-and-suspenders: DMs are 1:1 so this aside must stay empty.
    // Also drop any stale drawer classes so a prior server view can't
    // bleed through on mobile (bug batch 7q resurfacing guard).
    container.innerHTML = '';
    container.classList.remove('open');
    container.classList.remove('mobile-open');
    return;
  }

  const people = getCurrentServerPeople();
  const online = people.filter(p => presenceOf(p) !== 'offline');
  const offline = people.filter(p => presenceOf(p) === 'offline');
  const collapsed = loadCollapsed(sid);

  let html = `<div class="ml-header">
    <span>Members</span>
    <button class="ml-close-btn" id="ml-close-btn" title="Hide member list" aria-label="Hide member list">${iconX()}</button>
  </div>`;

  if (online.length > 0) {
    // Discord groups online members by their top role with the role's color on
    // the label. Owner first, then Admin, then remaining roles alphabetical,
    // then Member catch-all. Role color is looked up on state.roleColors
    // (person_id -> hex). Each group is collapsible; state persists in
    // localStorage per server.
    html += renderRoleGroups(online, sid, true, collapsed);
  }

  if (offline.length > 0) {
    const isCollapsed = collapsed.has('__offline__');
    html += `<div class="ml-group-label ml-group-collapsible${isCollapsed ? ' ml-group-collapsed' : ''}" data-group-key="__offline__" role="button" tabindex="0">
      <svg class="ml-group-caret" viewBox="0 0 24 24" width="10" height="10" fill="currentColor" aria-hidden="true"><path d="M7 10l5 5 5-5z"/></svg>
      Offline — ${offline.length}
    </div>`;
    html += `<div class="ml-group-body" data-group-body="__offline__"${isCollapsed ? ' style="display:none"' : ''}>` +
      offline.map(p => memberItem(p)).join('') + '</div>';
  }

  if (people.length === 0) {
    html += `<div class="ml-empty">
      <svg class="ml-empty-illus" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
      <div class="ml-empty-title">No members yet</div>
      <div class="ml-empty-sub">Invite people to get the conversation started.</div>
    </div>`;
  }

  container.innerHTML = html;

  // Profile-card click on every member row — single source of truth for opening
  // a profile card from any surface. The actual open call lives in profile-card.ts.
  container.querySelectorAll<HTMLElement>('.ml-member').forEach(row => {
    row.addEventListener('click', () => {
      const id = row.dataset['id'];
      if (id) openProfileCard(id, row);
    });

    // Right-click context menu (1b) on member rows
    row.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const id = row.dataset['id'];
      if (!id) return;
      const items: ContextMenuItem[] = [
        { label: 'View Profile', action: () => openProfileCard(id, row) },
        { label: 'Send Message', action: () => {
          window.dispatchEvent(new CustomEvent('hub-open-dm', { detail: { personId: id } }));
        }},
        { label: '', divider: true },
        { label: 'Copy User ID', action: () => {
          void navigator.clipboard.writeText(id).then(() => showToast('User ID copied', 'ok'));
        }},
      ];
      showContextMenu(e.clientX, e.clientY, items);
    });
  });

  // Collapsible role groups — toggle .ml-group-collapsed on the label and
  // hide/show the matching .ml-group-body. Persist to localStorage so the
  // state survives page reloads + channel swaps.
  container.querySelectorAll<HTMLElement>('.ml-group-collapsible').forEach(label => {
    const toggle = (): void => {
      const key = label.dataset['groupKey'];
      if (!key) return;
      const body = container.querySelector<HTMLElement>(`[data-group-body="${key}"]`);
      const nowCollapsed = !label.classList.contains('ml-group-collapsed');
      label.classList.toggle('ml-group-collapsed', nowCollapsed);
      if (body) body.style.display = nowCollapsed ? 'none' : '';
      const set = loadCollapsed(sid);
      if (nowCollapsed) set.add(key); else set.delete(key);
      saveCollapsed(sid, set);
    };
    label.addEventListener('click', toggle);
    label.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    });
  });

  // Close button — same behaviour as the .ca-header member toggle (mobile vs
  // desktop classes) so DM and channel views match Mitchell's expectation.
  document.getElementById('ml-close-btn')?.addEventListener('click', () => {
    const onMobile = window.innerWidth < 768;
    const cls = onMobile ? 'mobile-open' : 'open';
    container.classList.remove(cls);
    document.getElementById('members-toggle')?.classList.remove('active');
    if (onMobile) document.getElementById('sidebar-scrim')?.classList.remove('open');
    else if (sid) localStorage.setItem(`hub-people-members-open:${sid}`, 'false');
  });
}

/**
 * Bucket the online members by their top role and emit Discord-style
 * role-coloured group labels. Ordering: Owner → Admin → other roles
 * (alphabetical, stable) → Member. Empty buckets are skipped.
 */
function renderRoleGroups(people: Person[], serverId: string, online: boolean, collapsed: Set<string>): string {
  const buckets = new Map<string, Person[]>();
  for (const p of people) {
    const label = roleLabel(p, serverId);
    const arr = buckets.get(label) || [];
    arr.push(p);
    buckets.set(label, arr);
  }

  const ORDER = ['Owner', 'Admin'];
  const sorted: string[] = [];
  for (const k of ORDER) if (buckets.has(k)) sorted.push(k);
  const rest = [...buckets.keys()]
    .filter(k => !ORDER.includes(k) && k !== 'Member' && k !== 'bot')
    .sort((a, b) => a.localeCompare(b));
  sorted.push(...rest);
  if (buckets.has('bot')) sorted.push('bot');
  if (buckets.has('Member')) sorted.push('Member');

  let out = '';
  for (const role of sorted) {
    const arr = buckets.get(role)!;
    const color = roleColorForLabel(role);
    const labelText = role === 'bot' ? 'Bots' : role;
    const key = `role:${role}`;
    const isCollapsed = collapsed.has(key);
    out += `<div class="ml-group-label ml-group-collapsible${isCollapsed ? ' ml-group-collapsed' : ''}" data-group-key="${esc(key)}" role="button" tabindex="0" style="color:${color}">
      <svg class="ml-group-caret" viewBox="0 0 24 24" width="10" height="10" fill="currentColor" aria-hidden="true"><path d="M7 10l5 5 5-5z"/></svg>
      ${esc(labelText)} — ${arr.length}
    </div>`;
    out += `<div class="ml-group-body" data-group-body="${esc(key)}"${isCollapsed ? ' style="display:none"' : ''}>` +
      arr.map(p => memberItem(p, serverId)).join('') + '</div>';
  }
  // Silence unused parameter warning — 'online' is kept for API clarity even if
  // we don't render an "Online" header anymore (Discord merges role groups
  // under the online section).
  void online;
  return out;
}

/** Resolve a person's role label with Server Owner taking priority. */
function roleLabel(p: { id: string; role: string }, serverId: string): string {
  const server = state.servers.find(s => s.id === serverId);
  if (server && server.owner_id === p.id) return 'Owner';
  if (p.role === 'admin') return 'Admin';
  if (p.role === 'bot') return 'bot';
  if (p.role && p.role !== 'member') return p.role;
  return 'Member';
}

/**
 * Pick a display color for a role label. Owner/Admin have dedicated accents.
 * For custom roles we hash the label to a warm hue in the Discord palette so
 * the colour is stable across renders without needing per-role config yet.
 */
function roleColorForLabel(label: string): string {
  if (label === 'Owner') return 'var(--accent)';
  if (label === 'Admin') return 'var(--warning)';
  if (label === 'bot') return 'var(--info)';
  if (label === 'Member') return 'var(--text-muted)';
  // Deterministic palette pick for custom roles.
  const palette = [
    'var(--avatar-c1)', 'var(--avatar-c2)', 'var(--avatar-c6)',
    'var(--avatar-c7)', 'var(--avatar-c8)', 'var(--avatar-c11)',
  ];
  let h = 0;
  for (let i = 0; i < label.length; i++) h = ((h << 5) - h + label.charCodeAt(i)) | 0;
  const idx = Math.abs(h) % palette.length;
  return palette[idx] as string;
}

function memberItem(p: Person, serverId?: string): string {
  const initial = (p.name || '?').charAt(0).toUpperCase();
  const isBot = p.role === 'bot';
  const label = serverId ? roleLabel(p, serverId) : (p.role || 'Member');
  // Use the same color as the role group label so the member-list feels
  // consistent. Per-person overrides from state.roleColors win when present.
  const roleColor = state.roleColors[p.id] || roleColorForLabel(label);
  const presence = presenceOf(p);
  const botTag = isBot ? '<span class="ml-bot-tag">BOT</span>' : '';
  // Discord parity: when a custom_status is set, it replaces the role line
  // (the role chip moves to the hover-card / profile card). Otherwise show
  // the role label as before.
  const subLine = (p.custom_status || '').trim();
  const subHtml = subLine
    ? `<div class="ml-custom-status" title="${esc(subLine)}">${esc(subLine)}</div>`
    : `<div class="ml-role">${esc(label)}</div>`;

  return `<div class="ml-member" data-id="${esc(p.id)}" title="View profile">
    <div class="ml-avatar-wrap">
      <div class="ml-avatar" style="background:${avatarBg(p.id)}">${esc(initial)}</div>
      <div class="ml-status-dot ml-presence-${presence}" aria-label="${presence}"></div>
    </div>
    <div class="ml-info">
      <div class="ml-name" style="color:${roleColor}">${esc(p.name)}${botTag}</div>
      ${subHtml}
    </div>
  </div>`;
}
